import { FunctionComponent, memo } from "react";
import { Cell, Column, Heading, TableMenuSection } from "@edgetier/client-components";
import createPersistedState from "use-persisted-state";
import PerformanceIndicator from "../../performance-indicator";
import { IAgentComparisonData } from "types-for/agents/agents.types";
import { IProps } from "./comparison-table-tag-column.types";
import useTags from "hooks-for/tags/use-tags";
import InteractionMetricSummaryPercentageValue from "../../interaction-metric-summary-percentage-value";
import { PersistedStorageKeys } from "constants/persisted-storage-keys";
import ChooseTagForm from "components-for/agents/choose-tag-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import "./comparison-table-tag-column.scss";

const useTagSelected = createPersistedState<string[]>(PersistedStorageKeys.TagSelected);
const useTagGroupSelected = createPersistedState<string[]>(PersistedStorageKeys.TagGroupSelected);

/**
 * A column to display the percentage of handle interactions of an agent given a tag.
 * You can choose a tag within tag group and apply basic ordering to the values
 * @param props.overallComparisonData
 * @param props.showPercentage
 * @param props.isMaybeLoading
 * @returns Table column
 */
const ComparisonTableTagColumn: FunctionComponent<IProps> = ({
    overallComparisonData,
    showPercentage,
    isMaybeLoading,
    customColumnIndex = 0,
}) => {
    const { data: tags } = useTags();

    // Read persisted tag
    const [tagSelected, setTagSelected] = useTagSelected([]);
    // Read tag group and tag
    const [, setTagGroupSelected] = useTagGroupSelected([]);

    const selectedIndex = typeof customColumnIndex === "number" ? customColumnIndex : 0;
    // Find all tag details
    const tag = tags?.find((tag) => tag.tagId === tagSelected[selectedIndex]);

    /**
     * Remove the table column based on its index
     */
    const removeComparisonTableTagColumn = () => {
        setTagSelected((values) => {
            if (Array.isArray(values)) {
                return [...values.slice(0, selectedIndex), ...values.slice(selectedIndex + 1)];
            }
            return values;
        });
        setTagGroupSelected((values) => {
            if (Array.isArray(values)) {
                return [...values.slice(0, selectedIndex), ...values.slice(selectedIndex + 1)];
            }
            return values;
        });
    };

    return (
        <Column>
            <Heading
                sortProperty={`tagValues[${customColumnIndex}]`}
                menu={(closeMenu) => (
                    <TableMenuSection>
                        <div className="agents-table-tag-column">
                            <ChooseTagForm customColumnIndex={customColumnIndex} closeAction={closeMenu} />
                        </div>
                    </TableMenuSection>
                )}
            >
                {typeof tag === "undefined" ? (
                    <span>Choose Tag</span>
                ) : (
                    <span className="comparison-table-tag-column__selected-tag">{tag.tag}</span>
                )}
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        removeComparisonTableTagColumn();
                    }}
                    className="comparison-table-tag-column__remove-column-button"
                >
                    <FontAwesomeIcon title="remove-column" name="remove-column" icon={faTimes} />
                </button>
            </Heading>
            <Cell<IAgentComparisonData>>
                {({ isOverall, tagValues }) => {
                    return (
                        <PerformanceIndicator
                            overallValue={overallComparisonData?.tagValues?.[customColumnIndex] ?? null}
                            showPercentage={showPercentage && !isOverall}
                            value={tagValues?.[customColumnIndex] ?? null}
                        >
                            <InteractionMetricSummaryPercentageValue
                                isMaybeLoading={isMaybeLoading}
                                value={tagValues?.[customColumnIndex]}
                            />
                        </PerformanceIndicator>
                    );
                }}
            </Cell>
        </Column>
    );
};

export default memo(ComparisonTableTagColumn);
